import { useEffect, useLayoutEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';

/**
 * Multi-site: keeps the active site (authStore.activeSiteId → the X-Site-Id header) in sync with
 * the `?site=<siteId>` URL param. Mounted once inside the Router so deep links and reloads land on
 * the right site. When the active site CHANGES, it drops all cached server data so every query
 * refetches under the new X-Site-Id (the query keys aren't site-aware, so an explicit refetch is
 * what makes a site switch show the right content). Renders nothing.
 */
export function ActiveSiteSync(): null {
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const urlSite = params.get('site');
  const prev = useRef<string | null | undefined>(undefined);
  // The ACCOUNT, not only the tenant: impersonating a teammate inside the admin's own tenant keeps
  // the tenant id and changes the user, and queries like ['profile'] belong to the user.
  const tenant = useAuthStore((s) => `${s.tenantId ?? ''}:${s.userId ?? ''}`);
  const prevTenant = useRef<string | null | undefined>(undefined);

  /**
   * The TENANT changed — throw the cache away, do not merely refresh it.
   *
   * An admin opening a tenant's site starts impersonation, re-hydrates identity and navigates to
   * the Studio, all client-side. No reload, so every cached query from the admin's own tenant
   * survives into the impersonated session, and most of the keys are not tenant-aware — `['sites']`,
   * `['brands']`, `['pages']`, `['navigation']`, `['listings']` are all keyed by nothing but their
   * name, with the `X-Tenant-Slug` header doing the actual scoping. Until each one refetches, the
   * screen shows one tenant's data under another tenant's identity, which is the product's first
   * non-negotiable.
   *
   * `clear()`, not `invalidateQueries()`: invalidation leaves the stale entries readable while
   * they refetch, and for cross-tenant data "readable for one render" is exactly the thing that
   * must not happen. The cost is a cold start on a switch, which is the right price.
   *
   * Tenant-scoping forty query keys would fix it too, and should still happen — but one key at a
   * time leaves the un-migrated ones leaking, and this closes all of them at once.
   *
   * ## Why useLayoutEffect and not useEffect
   *
   * `useEffect` runs AFTER the browser paints. So the order would be: identity changes → everything
   * re-renders, new tenant in the header and the old tenant's data in the tables → paint → effect
   * fires → clear → re-render empty. One frame of the wrong tenant's brands and sites on screen,
   * under the right tenant's name, which is the most confusing possible version of this bug.
   *
   * `useLayoutEffect` runs after the DOM is updated and before the paint, and React flushes the
   * re-render it causes in the same pass — so that frame is never shown.
   *
   * Both paths that reach here today are arguably survivable without it: an impersonating admin is
   * already entitled to see both tenants, and a logout paints the login page rather than data. But
   * `logout()` resets the Zustand stores and has never touched this cache, so signing out and
   * signing in as someone else on the same browser is a genuine cross-USER carry-over, and the day
   * a non-admin tenant switcher exists the "they were allowed to see it anyway" argument quietly
   * stops being true. Not worth leaving to a future reader to notice.
   */
  useLayoutEffect(() => {
    if (prevTenant.current !== undefined && prevTenant.current !== tenant) queryClient.clear();
    prevTenant.current = tenant;
  }, [tenant, queryClient]);

  useEffect(() => {
    const next = urlSite || null;
    const current = useAuthStore.getState().activeSiteId;

    if (urlSite) {
      // The URL explicitly names a site → make it the active one.
      if (next !== current) useAuthStore.getState().setActiveSiteId(next);
      // On a site change, refresh what the new site should show.
      //
      // This used to also `removeQueries({queryKey: ['site-config']})`, because — in its own
      // words — "the site-config query keys aren't site-aware". They are now: every query whose
      // first key segment is `site-config` carries a site id, the Studio's through
      // `siteConfigKey(tenantId, siteId)` and the six panels through `['site-config', siteId, …]`.
      // Two sites cannot collide on one entry any more, so there is nothing stale to delete.
      //
      // Deleting was never free. `removeQueries` DISCARDS the other site's config outright, so
      // switching back re-fetched from nothing and the Studio sat empty while it waited. With the
      // entries kept, a switch back renders that site's cached config immediately and revalidates
      // behind it — which is the actual payoff of keying them, not just correctness.
      //
      // The blanket `invalidateQueries()` STAYS, and is not an oversight. Plenty of site-scoped
      // data is still keyed by tenant alone — `['pages']`, `['navigation']`, `['listings']`,
      // `['communities']` — and for those the header is the only thing telling one site's data
      // from another's, so they genuinely must refetch on a switch. Narrowing this is the same
      // work as site-scoping those keys, and it should shrink one family at a time rather than in
      // one leap.
      if (next !== prev.current) {
        prev.current = next;
        // After this commit's other effects, not during them. This component's effect can run
        // before a page's queries move to their new site-keyed entry, and invalidating then
        // refetched the OLD site's entry under the new X-Site-Id — a wasted call that also filed
        // one site's answer under the other's key. cancelRefetch: false — a query already
        // fetching its new entry keeps that fetch instead of being asked twice.
        // No cleanup clearing the timer: StrictMode's mount re-run would drop it, and the re-run
        // skips (prev already matches).
        setTimeout(() => {
          void queryClient.invalidateQueries(undefined, { cancelRefetch: false });
        }, 0);
      }
    } else {
      // No ?site in the URL (e.g. the "My Website" nav → /studio): PRESERVE the site you were
      // editing instead of snapping back to the primary. Only an explicit ?site (deep link,
      // "Edit in Studio", or the SiteSwitcher) changes the active site.
      prev.current = current;
    }
  }, [urlSite, queryClient]);

  return null;
}

export default ActiveSiteSync;
